/**
 * EmbedsList Component
 * Module 07 - Dashboard UI
 * Section 06 - Embeds Management (Step 6.6)
 * 
 * Grid display of all user embeds with loading and empty states
 */

import { useState, type FC } from 'react';
import { Plus } from 'lucide-react';
import EmbedCard from './EmbedCard';
import EmbedCodeModal from './EmbedCodeModal';
import EmbedPreview from './EmbedPreview';
import CreateEmbedModal from './CreateEmbedModal';
import EditEmbedModal from './EditEmbedModal';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import ConfirmDialog from '../common/ConfirmDialog';
import { useEmbeds } from '../../hooks';
import { useModels } from '../../hooks';
import { Embed } from '../../types';

/**
 * EmbedsList displays all embeds in a responsive grid
 * Handles create, edit, delete, preview, and code viewing
 */
const EmbedsList: FC = () => {
  // Hooks
  const { embeds, isLoading, deleteEmbed, toggleEmbedStatus, resetViewCount, refetch } = useEmbeds();
  const { models } = useModels();

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Selected embed states
  const [selectedEmbed, setSelectedEmbed] = useState<Embed | null>(null);
  const [embedToDelete, setEmbedToDelete] = useState<string | null>(null);

  /**
   * Handle create embed success
   */
  const handleCreateSuccess = () => {
    refetch();
    setCreateModalOpen(false);
  };

  /**
   * Handle view code
   */
  const handleViewCode = (embed: Embed) => {
    setSelectedEmbed(embed);
    setCodeModalOpen(true);
  };

  /**
   * Handle preview
   */
  const handlePreview = (embed: Embed) => {
    setSelectedEmbed(embed);
    setPreviewModalOpen(true);
  };

  /**
   * Handle edit (placeholder - would open edit modal)
   */
  const handleEdit = (embed: Embed) => {
    setSelectedEmbed(embed);
    setEditModalOpen(true);
  };

  /**
   * Handle delete confirmation
   */
  const handleDeleteClick = (embedId: string) => {
    setEmbedToDelete(embedId);
    setDeleteDialogOpen(true);
  };

  /**
   * Handle delete confirmation
   */
  const handleDeleteConfirm = async () => {
    if (embedToDelete) {
      await deleteEmbed(embedToDelete);
      setDeleteDialogOpen(false);
      setEmbedToDelete(null);
    }
  };

  /**
   * Handle toggle status
   */
  const handleToggleStatus = async (embedId: string, isActive: boolean) => {
    await toggleEmbedStatus(embedId, isActive);
  };

  /**
   * Handle reset view count
   */
  const handleResetViewCount = async (embedId: string) => {
    await resetViewCount(embedId);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Empty state
  if (embeds.length === 0) {
    return (
      <>
        <EmptyState
          message="Create your first embed to display 3D models on your website"
          action={
            <Button onClick={() => setCreateModalOpen(true)} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Embed
            </Button>
          }
        />

        <CreateEmbedModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          models={models}
        />
      </>
    );
  }

  // List view
  return (
    <>
      {/* Embeds Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {embeds.map((embed) => (
          <EmbedCard
            key={embed.id}
            embed={embed}
            onDelete={handleDeleteClick}
            onEdit={handleEdit}
            onViewCode={handleViewCode}
            onPreview={handlePreview}
            onToggleStatus={handleToggleStatus}
            onResetViewCount={handleResetViewCount}
          />
        ))}
      </div>

      {/* Modals */}
      <CreateEmbedModal
        open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          models={models}
        />

      {selectedEmbed && (
        <EditEmbedModal
          open={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedEmbed(null);
          }}
          embed={selectedEmbed}
        />
      )}

      {selectedEmbed && (
        <>
          <EmbedCodeModal
            open={codeModalOpen}
            onClose={() => {
              setCodeModalOpen(false);
              setSelectedEmbed(null);
            }}
            embedCode={selectedEmbed.embedCode}
            embedId={selectedEmbed.id}
            embedToken={selectedEmbed.embedToken}
          />

          <EmbedPreview
            open={previewModalOpen}
            onClose={() => {
              setPreviewModalOpen(false);
              setSelectedEmbed(null);
            }}
            embedId={selectedEmbed.id}
            embedToken={selectedEmbed.embedToken}
            embedName={selectedEmbed.name}
          />
        </>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setEmbedToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Embed"
        message="Are you sure you want to delete this embed? This action cannot be undone and the embed will stop working on any websites where it's currently used."
        confirmLabel="Delete"
        variant="danger"
      />
    </>
  );
};

export default EmbedsList;
